import { useCallback } from 'react';
import { useRouter } from 'next/router';
import {
  useApplicantLogMutation,
  ApplicantLogEventEnum,
} from '@vette/data-access';
import { growthbook, message } from '@vette/frontend-utils';

import { ActivityInLocalStorage } from '../types';

/**
 * Tracks of applicant activity and stores activity data in localStorage
 *
 * Exposes:
 * - getApplicationActivities: function for getting applicant's activities
 * - logActivity: function for loagging applicant's activities
 */
export const useLogApplicantActivity = (
  setActivityData: (data: ActivityInLocalStorage) => void
) => {
  const router = useRouter();

  const [applicantLog] = useApplicantLogMutation();

  const getPreviousActivities = useCallback(
    (activityData: ActivityInLocalStorage, jobOfferId: string) =>
      activityData?.activities &&
      jobOfferId in activityData.activities &&
      Array.isArray(activityData.activities[jobOfferId])
        ? (activityData?.activities?.[jobOfferId] as Array<string>)
        : [],
    []
  );

  const storeApplicantActivity = useCallback(
    (
      activityData: ActivityInLocalStorage,
      jobOfferId: string,
      activityId: string,
      previousActivities: string[]
    ) =>
      setActivityData({
        ...(activityData && activityData),
        activities: {
          ...(activityData?.activities && activityData.activities),
          [jobOfferId]: [...previousActivities, activityId],
        },
      }),
    [setActivityData]
  );

  const getApplicationActivities = useCallback(
    (activityData: ActivityInLocalStorage) => {
      const jobOfferId = router.query?.args?.[0];
      if (!jobOfferId) return [];
      return getPreviousActivities(activityData, jobOfferId);
    },
    [router, getPreviousActivities]
  );

  const clearApplicationActivities = useCallback(
    (activityData: ActivityInLocalStorage) => {
      const jobOfferId = router.query?.args?.[0];
      if (!jobOfferId) return;
      delete activityData?.activities?.[jobOfferId];
      setActivityData(activityData);
    },
    [router, setActivityData]
  );

  const logActivity = useCallback(
    async (activityData: ActivityInLocalStorage) => {
      const jobOfferId = router.query?.args?.[0];
      if (!jobOfferId) return;

      growthbook.appendAttributes({
        jobId: jobOfferId,
      });

      const previousActivities = getPreviousActivities(
        activityData,
        jobOfferId
      );
      const eventType = previousActivities.length
        ? ApplicantLogEventEnum.APPLICANT_SAW_INTERVIEW_NOW_PAGE_AGAIN
        : ApplicantLogEventEnum.APPLICANT_SAW_INTERVIEW_NOW_PAGE;

      const atc = router.query?.atc as string | undefined;
      const source = router.query?.source as string | undefined;

      const { data } = await applicantLog({
        variables: {
          input: {
            jobOfferId,
            eventType,
            atc,
            source,
          },
        },
      });

      if (data?.applicantLog?.__typename === 'GlobalActivityHistory') {
        if (atc && data?.applicantLog.metadata.parsedApplication) {
          const parsedApplication =
            data?.applicantLog.metadata.parsedApplication;

          growthbook.appendAttributes({
            id: parsedApplication.id,
            email: parsedApplication.email,
            flow: parsedApplication.phone ? 'PHONE' : 'EMAIL',
          });
        } else {
          growthbook.appendAttributes({
            flow: 'DIRECT_LINK',
          });
        }

        await storeApplicantActivity(
          activityData,
          jobOfferId,
          data.applicantLog.id,
          previousActivities
        );

        return data?.applicantLog?.__typename === 'GlobalActivityHistory'
          ? data?.applicantLog.metadata.data.parsedApplication
          : null;
      }

      message.error(
        'Something went wrong. Our team was notified and is intensively working on fixing the issue.'
      );
    },
    [router, storeApplicantActivity, getPreviousActivities, applicantLog]
  );

  return {
    clearApplicationActivities,
    getApplicationActivities,
    logActivity,
  };
};
